import React from 'react';
import { ClassroomProvider } from '@teetsh/app/src/containers/CahierJournal/DayColumn/ClassroomContext';
import { Lines } from '@teetsh/app/src/containers/CahierJournal/DayColumn/Lines';
import { AddEventItem } from './Seance/AddEventItem';
import setHours from '@teetsh/app/src/utils/time/setHours';
import { Droppable } from 'react-beautiful-dnd';
import tw from 'twin.macro';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { format } from '@teetsh/app/src/utils/time/format';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import { CJEvent } from '@teetsh/app/src/types/event';
import { ClassroomWithGroupeEvent } from '@teetsh/app/src/types/cj';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import startOfHour from 'utils/time/startOfHour';

interface Props {
  isMobile?: boolean;
  classrooms: Classroom[];
  classroom: ClassroomWithGroupeEvent & {
    eventsToDisplay: CJEvent[][][];
  };
  classroomsOrg: ClassroomsOrg;
  day: Date;
  collapsed: boolean;
  expanded?: boolean;
  globalScroll?: boolean;
  highlightedEventIds?: string[];
}

function isEmptyDay(groupedEvents: CJEvent[][][]) {
  if ((groupedEvents || []).length === 0) {
    return true;
  }
  const res = groupedEvents.some(g =>
    g.some(gg =>
      gg.some(
        ev =>
          ev.type === 'break' || ev.type === 'classic' || ev.type === 'rituel',
      ),
    ),
  );
  return !res;
}

export function ClassroomColumn(props: Props) {
  const allClassrooms = useClassrooms();
  const {
    classrooms,
    classroom,
    day,
    classroomsOrg,
    collapsed,
    expanded,
    highlightedEventIds = [],
  } = props;
  const dayDate = day;
  const dayString = format(day, 'fr-FR');
  const isEmptyDayForClassroom = isEmptyDay(classroom.groupedEvents);

  const cl = classroomsOrg === 'unified' ? classrooms : [classroom];
  return (
    <div
      data-testid={`seance classroom ${classroom.name}`}
      key={classroom.id}
      css={[
        tw`relative max-h-full flex-grow flex-1`,
        expanded && tw`min-w-[100px]`,
        !expanded && tw`min-w-0`,
        !isEmptyDayForClassroom && tw`min-h-[50px]`,
      ]}
    >
      <div tw="flex flex-col max-h-full">
        <ClassroomProvider classroom={classroom}>
          {allClassrooms.length > 1 && (
            <h4 tw="shrink-0 mb-4 text-center truncate">{classroom.name}</h4>
          )}
          <div tw="flex-auto min-w-0">
            {classroom.eventsToDisplay.map((listOfEvents, i) => (
              <Lines
                index={i}
                key={i}
                isMobile={props.isMobile}
                collapsed={collapsed}
                dayString={dayString}
                classroomsOrg={classroomsOrg}
                classrooms={cl}
                listOfEvents={listOfEvents}
                classroom={classroom}
                highlightedEventIds={highlightedEventIds}
              />
            ))}

            {/* in case there is no event, we still need a droppable zone to allow the user to add an event */}
            {isEmptyDayForClassroom && (
              <Droppable
                droppableId={`${dayString}_${classroom.id}_empty`}
                key={classroom.id}
              >
                {provided => (
                  <div
                    key={classroom.id}
                    tw="w-full flex flex-col"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <AddEventItem
                      extraTopMargin={
                        //  C'est principalement pour gérer le cas où une colonne a des évènements et l'autre pas
                        // c'est pour aligner avec le bouton d'ajout de séance lorsqu'il y a d'autre séances dans
                        // la colonne
                        classrooms.length > 0 && classroomsOrg === 'split'
                      }
                      classrooms={cl}
                      visible
                      start={setHours(startOfHour(dayDate), 8)}
                      end={setHours(startOfHour(dayDate), 9)}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </div>
        </ClassroomProvider>
      </div>
    </div>
  );
}
